import React, { useState, useEffect } from 'react';

const CanYouOutsmartRobot = () => {
  const [showIntro, setShowIntro] = useState(true);
  const questions = [
    {
      question: "I shine during the day and help plants grow. What am I?",
      answer: "sun",
      robot: "sun"
    },
    {
      question: "I have pages and words. I help you learn. What am I?",
      answer: "book",
      robot: "book"
    },
    {
      question: "I'm full of numbers, used in math. What am I?",
      answer: "calculator",
      robot: "calculator"
    },
    {
      question: "You sit on me in a classroom. I usually have four legs. What am I?",
      answer: "chair",
      robot: "desk"
    },
    {
      question: "I help you see tiny things. I'm used in science class. What am I?",
      answer: "microscope",
      robot: "telescope"
    },
    {
      question: "I fly in the sky but I'm not a bird. I carry passengers. What am I?",
      answer: "airplane",
      robot: "helicopter"
    }
  ];

  const [current, setCurrent] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [robotResponse, setRobotResponse] = useState('');
  const [userFeedback, setUserFeedback] = useState('');

  useEffect(() => {
    setUserInput('');
    setRobotResponse('');
    setUserFeedback('');
  }, [current]);

  const handleRobotAnswer = () => {
    const correct = questions[current].answer;
    const robot = questions[current].robot;
    const feedback = userInput.trim().toLowerCase() === correct ? '✅ Correct!' : `❌ Correct answer is: ${correct}`;
    setRobotResponse(`🤖 Robot says: ${robot}`);
    setUserFeedback(feedback);
  };

  const handleNext = () => {
    setCurrent((current + 1) % questions.length);
  };

  const styles = {
    body: {
      margin: 0,
      fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
      background: 'radial-gradient(circle at top left, #E3F2FD, #FCE4EC)',
      color: '#333',
      animation: 'fadeIn 1s ease-in'
    },
    container: {
      maxWidth: '1100px',
      margin: 'auto',
      padding: '60px 30px',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    leftContent: {
      flex: 1,
      paddingRight: '30px'
    },
    rightImage: {
      flex: 1,
      textAlign: 'center'
    },
    image: {
      maxWidth: '90%',
      borderRadius: '16px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)'
    },
    h1: {
      fontSize: '3rem',
      color: '#4A148C',
      marginBottom: '20px'
    },
    highlight: {
      backgroundColor: '#FFF3E0',
      padding: '15px 20px',
      borderLeft: '6px solid #FFB74D',
      borderRadius: '10px',
      marginBottom: '25px',
      fontSize: '1.2rem'
    },
    quote: {
      fontStyle: 'italic',
      color: '#616161',
      marginTop: '20px'
    },
    startBtn: {
      display: 'inline-block',
      marginTop: '40px',
      padding: '16px 34px',
      fontSize: '1.3rem',
      backgroundColor: '#4FC3F7',
      color: 'white',
      border: 'none',
      borderRadius: '14px',
      cursor: 'pointer',
      transition: 'background 0.3s, transform 0.3s'
    },
    quizContainer: {
      // maxWidth: '1000px',
      margin: 'auto',
      padding: '40px 20px',
      textAlign: 'center',
      fontFamily: 'Comic Sans MS',
      background: 'linear-gradient(to right, #FFF3E0, #E1F5FE)',
      color: '#333'
    },
    questionBox: {
      background: '#E1F5FE',
      padding: '20px',
      borderRadius: '12px',
      fontSize: '1.2rem',
      margin: '30px 0'
    },
    chatBox: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: '30px',
      marginTop: '40px',
      alignItems: 'flex-start'
    },
    side: {
      width: '48%',
      background: '#F1F8E9',
      padding: '20px',
      borderRadius: '16px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
    },
    avatar: {
      width: '60px',
      height: '60px',
      borderRadius: '50%',
      marginBottom: '10px'
    },
    label: {
      fontWeight: 'bold',
      marginBottom: '10px',
      fontSize: '1.2rem'
    },
    input: {
      width: '80%',
      padding: '10px',
      fontSize: '1rem',
      borderRadius: '8px',
      border: '1px solid #ccc'
    },
    button: {
      marginTop: '10px',
      padding: '10px 20px',
      fontSize: '1rem',
      backgroundColor: '#FFD54F',
      color: '#333',
      border: 'none',
      borderRadius: '10px',
      cursor: 'pointer'
    },
    feedback: {
      marginTop: '15px',
      fontWeight: 'bold',
      fontSize: '1.1rem',
      minHeight: '30px'
    },
    nextBtn: {
      marginTop: '40px',
      padding: '10px 20px',
      fontSize: '1rem',
      backgroundColor: '#4FC3F7',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer'
    }
  };

  if (showIntro) {
    return (
      <div style={styles.body}>
        <div style={styles.container}>
          <div style={styles.leftContent}>
            <h1 style={styles.h1}>🤔 Can You Outsmart a Robot?</h1>
            <div style={styles.highlight}>
              Over 70 years ago, <strong>Alan Turing</strong> had a big question: <br />
              <em>"Can a machine think like a human?"</em>
            </div>
            <div style={styles.highlight}>
              To find out, he created something called the <strong>Turing Test</strong>.<br />
              <em>If a computer can answer questions so well that you think it’s a human...</em><br />
              then maybe it’s truly smart!
            </div>
            <div style={styles.quote}>
              "Machines can think... if we teach them how." — Alan Turing
            </div>
            <button style={styles.startBtn} onClick={() => setShowIntro(false)}>🎮 Start the Challenge</button>
          </div>
          <div style={styles.rightImage}>
            <img style={styles.image} src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a1/Alan_Turing_Aged_16.jpg/440px-Alan_Turing_Aged_16.jpg" alt="Alan Turing" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.quizContainer}>
      <h1 style={styles.h1}>Can You Guess Better Than a Robot?</h1>
      <div style={styles.questionBox}>🤔 <span>{questions[current].question}</span></div>
      <div style={styles.chatBox}>
        <div style={styles.side}>
          <img src="https://cdn-icons-png.flaticon.com/512/3135/3135768.png" alt="User" style={styles.avatar} />
          <div style={styles.label}>Your Answer</div>
          <input
            type="text"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            placeholder="Type your guess here..."
            style={styles.input}
          />
          <div style={styles.feedback}>{userFeedback}</div>
        </div>
        <div style={styles.side}>
          <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a1/Alan_Turing_Aged_16.jpg/330px-Alan_Turing_Aged_16.jpg" alt="Robot" style={styles.avatar} />
          <div style={styles.label}>Robot's Answer</div>
          <button style={styles.button} onClick={handleRobotAnswer}>What does the Robot Think?</button>
          <div style={styles.feedback}>{robotResponse}</div>
        </div>
      </div>
      <button style={styles.nextBtn} onClick={handleNext}>Next Challenge ➡️</button>
    </div>
  );
};

export default CanYouOutsmartRobot;
